/**
 * Data layer for `creative-preview`. Thin typed fetch wrappers over
 * /api/v1/creative-preview. Raw `fetch()` is confined to this file per the
 * dash contract.
 *
 * Backend shape (see apps/api/src/routes/creative-preview.ts):
 *   POST   /api/v1/creative-preview        -> create (creativeId[, expiresInMinutes])
 *   GET    /api/v1/creative-preview        -> list active previews for tenant
 *   GET    /api/v1/creative-preview/:token -> resolve (public-ish)
 *   DELETE /api/v1/creative-preview/:token -> revoke
 */

export interface CreativePreviewRow {
	tenant_id: string;
	token: string;
	creative_id: string;
	storage_key: string;
	mime: string;
	expires_at: number;
	revoked_at: number | null;
	created_at: number;
}

export interface ListCreativePreviewResponse {
	previews: CreativePreviewRow[];
}

export interface CreateCreativePreviewBody {
	creativeId: string;
	expiresInMinutes?: number;
}

export interface CreateCreativePreviewResponse {
	previewUrl: string;
	token: string;
	expiresAt: number;
	creativeId: string;
	expiresInMinutes: number;
}

export interface RevokeCreativePreviewResponse {
	revoked: boolean;
	token: string;
}

const BASE = "/api/v1/creative-preview";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function list(): Promise<ListCreativePreviewResponse> {
	const res = await fetch(BASE, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListCreativePreviewResponse>(res);
}

export async function create(
	body: CreateCreativePreviewBody,
): Promise<CreateCreativePreviewResponse> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body),
	});
	return parseOrThrow<CreateCreativePreviewResponse>(res);
}

export async function remove(
	token: string,
): Promise<RevokeCreativePreviewResponse> {
	const res = await fetch(`${BASE}/${encodeURIComponent(token)}`, {
		method: "DELETE",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<RevokeCreativePreviewResponse>(res);
}

/**
 * Regenerate a preview link: revoke the current token then create a fresh one
 * for the same creative, optionally with a new expiry window.
 */
export async function regenerate(args: {
	token: string;
	creativeId: string;
	expiresInMinutes?: number;
}): Promise<CreateCreativePreviewResponse> {
	await remove(args.token).catch(() => undefined);
	return create({
		creativeId: args.creativeId,
		expiresInMinutes: args.expiresInMinutes,
	});
}

/**
 * Build the shareable URL for a preview token on the current origin. The API
 * stores nothing but the token; the frontend rebuilds the full URL so links
 * travel with whichever surface (staging / prod) the dashboard runs on.
 */
export function buildPreviewUrl(token: string): string {
	if (typeof window !== "undefined" && window.location?.origin) {
		return `${window.location.origin}${BASE}/${encodeURIComponent(token)}`;
	}
	return `${BASE}/${encodeURIComponent(token)}`;
}

export const creativePreviewQueryKeys = {
	all: ["creative-preview"] as const,
	list: () => [...creativePreviewQueryKeys.all] as const,
	detail: (token: string) =>
		[...creativePreviewQueryKeys.all, token] as const,
};
